/**
 * The one word tokenizer and the one fold of the redline code.
 *
 * Every comparison in the engine, the proposal builder, the three-way merge,
 * the paragraph locator and the anchor tracker is made in words, and every
 * one of them cuts words the same way: a word is a maximal run of non-space
 * characters, whitespace of any kind separates words. Keeping the rule here
 * means a tab, a double space or a line break is one boundary everywhere, so
 * the diff, the similarity score and the bookmark trim can never disagree
 * about where a word starts.
 *
 * The one exception is the redline diff's units (`diffUnits`), which also cut
 * the words of scripts written without spaces between them.
 * `diffUnitsWithTrailingSeparators` gives those units with the whitespace after
 * each, for a caller that takes a text apart in the diff's units and puts it back.
 *
 * Two words are the same word when their comparison keys are equal
 * (`comparisonKey`). The key is the fold every reading of the text shares: the
 * characters a reader cannot tell apart folded onto one spelling, so the anchor
 * finder, the planner, the landing audit and the collision reader all read
 * "Supplier's" and "Supplier’s" as one word and none of them writes the one over
 * the other. `foldForMatching` is the same fold with the map back to the text's
 * own characters, for a caller that finds words in the folded text and then cuts
 * the document's characters.
 */

/** A word and its character offsets in the text it was cut from, so a word-level decision can be mapped back to characters. */
export interface WordToken {
    text: string;
    startOffset: number;
    endOffset: number;
}

/**
 * A redline diff unit (`WordTokenizer.diffUnits`) with its character offsets
 * and the whitespace that stands between it and the next unit of the text it
 * was cut from. Between two words of one Japanese, Chinese or Thai run that
 * whitespace is "", so units taken out of a text are put back with exactly
 * the characters that stood between them and no space is invented.
 */
export interface DiffUnitWithTrailingSeparator extends WordToken {
    trailingSeparator: string;
}

/**
 * A word cut where the punctuation at its end begins: everything up to and
 * including its last letter, digit or combining mark, and the punctuation after it.
 */
export interface WordWithTrailingPunctuation {
    textBeforeTrailingPunctuation: string;
    trailingPunctuation: string;
}

/**
 * A text folded for matching, with the map back to the characters it was folded
 * from: `sourceStartByFoldedIndex[i]` is where folded character `i` begins in the
 * original and `sourceEndByFoldedIndex[i]` is one past where it ends. They differ
 * by more than one where a run of whitespace folded to a single space, or a
 * character stored as several folded to one.
 */
export interface FoldedText {
    text: string;
    sourceStartByFoldedIndex: number[];
    sourceEndByFoldedIndex: number[];
}

/** One grapheme cluster of a text, what a reader takes for one character, and the offset in the text it starts at. */
interface GraphemeCluster {
    text: string;
    startOffset: number;
}

/** A run of consecutive code points, both ends included. */
interface CodePointBlock {
    firstCodePoint: number;
    lastCodePoint: number;
}

/**
 * The last letter, digit or combining mark of a word and every character after it,
 * none of which is one of those.
 *
 * WHY marks
 *   An accent stored as its own character ("Nestlé") belongs to the letter
 *   before it. Counted as punctuation, adding or changing it was drawn as a change
 *   to the bare mark instead of to the word.
 */
const LAST_WORD_CHARACTER_AND_TRAILING_PUNCTUATION = /[\p{L}\p{N}\p{M}][^\p{L}\p{N}\p{M}]*$/u;

/**
 * The punctuation a word opens or closes with, taken off for its content key:
 * everything before its first letter, digit or mark and after its last.
 *
 * EXAMPLE
 *   "“Order”," -> "Order"      "(45)," -> "45"      "Nestlé," -> "Nestlé"
 */
const PUNCTUATION_AT_WORD_EDGES = /^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu;

/**
 * A sentence's closing marks as the fold reads them (`endsSentence`): a sentence
 * terminal, Unicode's Sentence_Terminal (". ! ? 。 ‼ ؟ ।" and the rest; a full-width
 * "．" or "？" has folded onto its ASCII mark), followed only by the closing quotes and
 * brackets a sentence may end inside. Curly quotes have folded onto '"' and "'"; a
 * quote the fold keeps ("«", "»", "「」") is an opening or closing quotation mark
 * (Pi, Pf) or a closing bracket (Pe).
 *
 * EXAMPLE, read on the folded marks
 *   "."  "?\""  ".)"  "。」"  ".\"" (from German ".“")  -> a sentence end
 *   ","  ";"   ":"   ")"   "-"                         -> not one
 */
const SENTENCE_TERMINAL_THEN_CLOSERS = /\p{Sentence_Terminal}["'\p{Pe}\p{Pf}\p{Pi}]*$/u;

/**
 * The same, where a semicolon also closes a clause the way a terminal closes a sentence
 * (`endsSentenceOrSemicolonClause`); "；" has folded onto ";".
 *
 * EXAMPLE, read on the folded marks
 *   "interest;"  "pay."  "とする。」"  -> ends one      "terms:"  "fees,"  -> does not
 */
const SENTENCE_TERMINAL_OR_SEMICOLON_THEN_CLOSERS = /[\p{Sentence_Terminal};]["'\p{Pe}\p{Pf}\p{Pi}]*$/u;

/**
 * A mark a sentence writes once and never twice in a row (`isPunctuationWrittenOnce`):
 * Unicode's Terminal_Punctuation, the full stop, comma, semicolon, colon, question and
 * exclamation marks in every script ("。" "、" "，" "؛" "।" among them).
 */
const TERMINAL_PUNCTUATION = /^\p{Terminal_Punctuation}$/u;

/**
 * One character of a diff unit as the content-word runs read it (`charactersTheRunsRead`):
 * a character with the marks NFC composes into it, or one character on its own, with
 * whether the runs take it and where in the unit it sits.
 */
interface CharacterTheRunsRead {
    isLetterOrDigit: boolean;
    startOffset: number;
    endOffset: number;
}

/**
 * A mark that closes a sentence or a clause (`closesSentenceOrClauseBeforeASpace`):
 * Unicode's Sentence_Terminal (". ! ?", "।", "؟", "‼" and the rest), the semicolon or
 * the colon.
 */
const MARK_CLOSING_SENTENCE_OR_CLAUSE = /^[\p{Sentence_Terminal};:]$/u;

/** One letter, in any script. */
const ONE_LETTER = /^\p{L}$/u;

/**
 * A character of a script written without spaces between its words: Chinese,
 * Japanese, Thai, Lao, Khmer and Myanmar. Script_Extensions (`scx`) so the
 * long-vowel mark ー, whose Script is Common, counts as the kana it is written in.
 */
const CHARACTER_OF_SCRIPT_WITHOUT_SPACES = /[\p{scx=Han}\p{scx=Hiragana}\p{scx=Katakana}\p{scx=Thai}\p{scx=Lao}\p{scx=Khmer}\p{scx=Myanmar}]/u;

/**
 * The full-width forms of the ASCII punctuation marks: U+FF01 to U+FF5E with the
 * full-width digits and letters left out. Chinese and Japanese text sets these marks
 * wide, and a model retyping a quote writes them in their ASCII form.
 *
 * Not here
 *   - "、" (U+3001) and "。" (U+3002): marks of their own, not wide forms of "," and ".";
 *     a quote that retypes them is refused and told to quote them exactly
 *   - "０"-"９", "Ａ"-"Ｚ", "ａ"-"ｚ": digits and letters are words, and a different
 *     character is a different word
 *   - U+3000, the ideographic space: already whitespace to the fold
 */
const FULL_WIDTH_PUNCTUATION_BLOCKS: CodePointBlock[] = [
    { firstCodePoint: 0xff01, lastCodePoint: 0xff0f },
    { firstCodePoint: 0xff1a, lastCodePoint: 0xff20 },
    { firstCodePoint: 0xff3b, lastCodePoint: 0xff40 },
    { firstCodePoint: 0xff5b, lastCodePoint: 0xff5e },
];

/**
 * How far above its ASCII mark a full-width mark sits: the full-width forms run parallel
 * to ASCII from U+FF01 ("！") over U+0021 ("!").
 *
 * EXAMPLE
 *   "，" U+FF0C - 0xFEE0 = U+002C ","
 *   "（" U+FF08 - 0xFEE0 = U+0028 "("
 */
const FULL_WIDTH_TO_ASCII_OFFSET = 0xfee0;

/** Every full-width punctuation mark with the ASCII mark it folds onto. */
const FULL_WIDTH_PUNCTUATION_FOLDS: Array<[string, string]> = FULL_WIDTH_PUNCTUATION_BLOCKS.flatMap(({ firstCodePoint, lastCodePoint }) =>
    /**
     * EXAMPLE
     *   block U+FF1A..U+FF20 -> length 0xff20 - 0xff1a + 1 = 7: "：" "；" "＜" "＝" "＞" "？" "＠";
     *   the `+ 1` counts the last code point, both ends being in the block
     */
    Array.from({ length: lastCodePoint - firstCodePoint + 1 }, (_unused, offsetInBlock): [string, string] => {
        const fullWidthCodePoint = firstCodePoint + offsetInBlock;
        return [String.fromCodePoint(fullWidthCodePoint), String.fromCodePoint(fullWidthCodePoint - FULL_WIDTH_TO_ASCII_OFFSET)];
    }),
);

/**
 * The characters a model standardises when it retypes a quote, each folded onto the
 * spelling a keyboard produces, so an anchor that names the right words is not
 * refused over one of them and a word retyped with one of them is not struck and
 * written again.
 *
 * CASES
 *   - the four typographic single quotes and the apostrophe -> '
 *   - the four typographic double quotes                    -> "
 *   - every dash and the minus sign                         -> -
 *   - the full-width form of an ASCII punctuation mark      -> that mark (`FULL_WIDTH_PUNCTUATION_FOLDS`)
 *
 * Only characters that read as the same mark are here. Nothing that changes a word
 * belongs in this table: the fold decides where an edit lands and what it keeps, so a
 * pair folded together here can never be told apart again.
 */
const FOLDED_PUNCTUATION = new Map<string, string>([
    ["‘", "'"], ["’", "'"], ["‚", "'"], ["‛", "'"], ["ʼ", "'"],
    ["“", '"'], ["”", '"'], ["„", '"'], ["‟", '"'],
    ["‐", "-"], ["‑", "-"], ["‒", "-"], ["–", "-"], ["—", "-"], ["―", "-"], ["−", "-"],
    ...FULL_WIDTH_PUNCTUATION_FOLDS,
]);

/**
 * What counts as whitespace when folding: every character `\s` matches, plus the
 * spaces Word writes that a model retypes as an ordinary one — the non-breaking
 * space above all, which `\s` does match but which is named here so the intent is
 * on the page. The engine, the operations and the planner read it through
 * `isWhitespace`, `isWhitespaceOnly` and the whitespace run counts, so a tab or a
 * line break is a gap between words wherever a space is.
 */
const MATCHED_AS_WHITESPACE = /[\s   ﻿]/;

/** A line break inside a run of whitespace, which the prompt shows as a line break (`<br/>`): the one whitespace the fold must not read as a space (`whitespaceReadsTheSame`). */
const LINE_BREAK_IN_WHITESPACE = /[\r\n]/;

/** A letter or a digit anywhere in a text: what makes the text a word rather than punctuation or spacing (`holdsALetterOrDigit`). */
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/** The schemes a word opens with when it is a web address a reader opens (`webAddressesIn`), in any case. */
const WEB_ADDRESS_SCHEME = /^https?:\/\//iu;

/**
 * A word of printable ASCII and nothing else: no whitespace, no character the fold
 * changes. The key of such a word is the word, and most English words are one.
 */
const PLAIN_ASCII_WORD = /^[!-~]*$/;

/**
 * A character with the marks that follow it, or marks with no character before them: the
 * clusters the fold reads on a host with no `Intl.Segmenter`.
 */
const CHARACTER_WITH_ITS_MARKS = /\P{M}\p{M}*|\p{M}+/gu;

export class WordTokenizer {
    /**
     * The word segmenter `diffUnits` cuts with, made on its first use and kept for
     * every cut after it.
     *
     * WHY one
     *   Making a segmenter costs more than the cut it makes, and a segmenter keeps no
     *   state from one cut to the next. The locator cuts every paragraph of a document
     *   at every tier, so a new segmenter per cut was most of that cost.
     *
     * NULL
     *   before the first cut of a text holding a Japanese, Chinese or Thai character
     */
    private static japaneseWordSegmenter: Intl.Segmenter | null = null;

    /**
     * The grapheme segmenter every mapped fold cuts with (`foldForMatching`), made on
     * the first fold.
     *
     * WHY one, made lazily
     *   Every anchor reading folds the paragraph and the quote, and building a segmenter
     *   cost as much as cutting with it: 191 ms against 104 ms per 10,000 folds of a
     *   117-character paragraph. A host that never folds never builds one.
     *
     * NULL
     *   before the first mapped fold
     */
    private static graphemeSegmenter: Intl.Segmenter | null = null;

    /** The words of `text`, in order, without their positions. */
    static words(text: string): string[] {
        return text.match(/\S+/g) ?? [];
    }

    /** The words of `text` with the character offsets each occupies in it. */
    static positioned(text: string): WordToken[] {
        const wordTokens: WordToken[] = [];
        const wordPattern = /\S+/g;
        let wordMatch = wordPattern.exec(text);
        while (wordMatch) {
            // `[0]` is the whole match: the run of non-space characters, since the pattern has no groups.
            const wordText = wordMatch[0];
            const wordStartOffset = wordMatch.index;
            const wordEndOffset = wordStartOffset + wordText.length;
            const wordToken = { text: wordText, startOffset: wordStartOffset, endOffset: wordEndOffset };
            /**
             * EXAMPLE
             *   text        = "a bc"
             *   wordMatch[0]= "bc"
             *   startOffset = 2
             *   length      = 2
             *   endOffset   = 2 + 2 = 4
             *
             *   endOffset is the first character after the word, not the last character in it.
             */
            wordTokens.push(wordToken);
            wordMatch = wordPattern.exec(text);
        }
        return wordTokens;
    }

    /**
     * The units the redline diff compares: `positioned`'s runs of non-space
     * characters, with every run that holds a character of a script written
     * without spaces cut further into that script's words (`Intl.Segmenter`).
     *
     * WHY
     *   A Japanese or Chinese clause has no spaces, so as one run it is one unit,
     *   and changing two characters in it struck and rewrote the whole clause. Cut
     *   into words, the diff strikes 製品 and inserts 商品 and keeps the rest.
     *
     * CASES, per run of non-space characters
     *   - no character of such a script -> the run, exactly as `positioned` gives it
     *   - one or more                   -> each segment of the run, punctuation included,
     *                                      at its offsets in `text`
     *
     * EXAMPLE
     *   text    = "Article 5 本製品の"
     *   runs    = "Article" 0..7, "5" 8..9, "本製品の" 10..14
     *   the third run's segments at 0, 1 and 3 of the run -> "本" 10..11, "製品" 11..13, "の" 13..14
     *
     * FALLBACK
     *   A host with no `Intl.Segmenter` keeps each run whole, the units every
     *   redline was drawn in before this cut existed.
     */
    static diffUnits(text: string): WordToken[] {
        const runs = WordTokenizer.positioned(text);
        if (typeof Intl.Segmenter !== "function") return runs;
        return runs.flatMap((run) => {
            if (!CHARACTER_OF_SCRIPT_WITHOUT_SPACES.test(run.text)) return [run];
            /**
             * CASES
             *   - the first run of such a script any cut meets -> the segmenter is made now and kept
             *   - every later one                              -> the kept segmenter
             */
            WordTokenizer.japaneseWordSegmenter ??= new Intl.Segmenter("ja", { granularity: "word" });
            return Array.from(WordTokenizer.japaneseWordSegmenter.segment(run.text), (segment) => ({
                text: segment.segment,
                /**
                 * EXAMPLE
                 *   run.startOffset = 10, segment.index = 1, segment "製品" (length 2)
                 *   startOffset     = 10 + 1 = 11
                 *   endOffset       = 11 + 2 = 13, the first character after the segment
                 */
                startOffset: run.startOffset + segment.index,
                endOffset: run.startOffset + segment.index + segment.segment.length,
            }));
        });
    }

    /**
     * The redline diff's units of `text` (`diffUnits`), each with its offsets and
     * the whitespace that follows it, for a caller that compares texts unit by
     * unit and builds a text back out of the units it keeps. It is the package's
     * one word-plus-separator cut, in the engine's units, so a Japanese, Chinese or
     * Thai clause is several units, not one word.
     *
     * RETURNS
     *   The units in order. Joining the text before the first unit, then every
     *   `text + trailingSeparator`, then the text after the last unit, reproduces
     *   `text` character for character: tabs, non-breaking spaces, line breaks and
     *   the text's leading and trailing whitespace are all where they were.
     *
     * EXAMPLE
     *   text  "Article 5\t本製品の。 "
     *   unit       offsets   trailingSeparator
     *   Article    0..7      " "
     *   5          8..9      "\t"
     *   本         10..11    ""     (the next unit stands right after it in the same run)
     *   製品       11..13    ""
     *   の         13..14    ""
     *   。         14..15    ""     (the last unit; the trailing " " is text.slice(15))
     *
     * EDGE CASE
     *   The last unit carries "" and not the text's trailing whitespace, so a
     *   caller joining units from several texts decides itself what separates
     *   one text's last unit from the next text's first; the trailing whitespace
     *   is `text.slice(lastUnit.endOffset)`.
     *
     * NULL
     *   A text with no unit (empty, or whitespace only) gives [].
     *
     * Compare two units by `WordTokenizer.comparisonKey(unit.text)`, never by
     * their raw text.
     */
    static diffUnitsWithTrailingSeparators(text: string): DiffUnitWithTrailingSeparator[] {
        const units = WordTokenizer.diffUnits(text);
        return units.map((unit, unitIndex) => {
            /**
             * EXAMPLE
             *   unitIndex = 1, units.length = 6
             *   `unitIndex + 1` -> 2, the unit right after this one ("本" after "5")
             */
            const nextUnit = units[unitIndex + 1];
            /**
             * CASES
             *   - another unit follows -> the characters from this unit's end to that unit's start:
             *                             "\t" between "5" (ends 9) and "本" (starts 10),
             *                             "" between "本" (ends 11) and "製品" (starts 11)
             *   - this is the last unit -> "", the text's own trailing whitespace is not carried
             */
            const trailingSeparator = nextUnit === undefined ? "" : text.slice(unit.endOffset, nextUnit.startOffset);
            return { text: unit.text, startOffset: unit.startOffset, endOffset: unit.endOffset, trailingSeparator };
        });
    }

    /**
     * The runs of letters and digits inside each unit of `text` (`letterOrDigitRunsOf`),
     * in order, each at its offsets in the text: the words a link's display text and the
     * text written over it are compared by (`LinkFieldStriker`).
     *
     * WHY the diff's units first
     *   Japanese, Chinese and Thai write no space between words, so read as runs of
     *   letters and digits alone "別紙1" was one word: "詳細は[別紙1]。" -> "詳細は別紙2。"
     *   wrote none of the link's words back, and the link was struck and left empty
     *   (R17c follow-up 3, 2026-10-16). Cut as the diff cuts it, "別紙" stands apart from
     *   "1", and only the number changes. A spaced script's unit is a word with its
     *   punctuation, which the runs then shed.
     *
     * EXAMPLE
     *   "詳細は別紙1。"                  -> 詳細 0..2, は 2..3, 別紙 3..5, 1 5..6
     *   "Fee's Schedule,"               -> Fee 0..3, s 4..5, Schedule 6..14
     *   "Re\u{301}sume\u{301}s."        -> one run 0..9: the accents compose into their letters
     */
    static contentWordRuns(text: string): WordToken[] {
        return WordTokenizer.diffUnits(text).flatMap((unit) =>
            WordTokenizer.letterOrDigitRunsOf(unit.text).map((wordRun) => ({
                text: wordRun.text,
                /** The run's offsets in the text: the unit's start plus the run's place inside the unit. */
                startOffset: unit.startOffset + wordRun.startOffset,
                endOffset: unit.startOffset + wordRun.endOffset,
            })));
    }

    /**
     * The runs of letters and digits of one diff unit, each at its offsets in the unit,
     * every character read as the fold reads it with the marks stored after it
     * (`charactersTheRunsRead`).
     *
     * EXAMPLE
     *   "Re\u{301}sume\u{301}s"    -> Re\u{301}sume\u{301}s 0..9
     *   "अनुबंध"                  -> अन 0..2, ब 3..4, ध 5..6
     */
    private static letterOrDigitRunsOf(unitText: string): WordToken[] {
        const wordRuns: WordToken[] = [];
        for (const characterTheRunsRead of WordTokenizer.charactersTheRunsRead(unitText)) {
            /** WHEN the character is no letter or digit CONSEQUENCE no run takes it, and the next letter opens a run of its own. */
            if (!characterTheRunsRead.isLetterOrDigit) continue;
            const lastWordRun = wordRuns[wordRuns.length - 1];
            /**
             * CASES
             *   - the last run ends where this character starts -> the run grows to take it
             *   - nothing, or a character no run takes, between  -> it opens a run
             */
            if (lastWordRun !== undefined && lastWordRun.endOffset === characterTheRunsRead.startOffset) {
                wordRuns[wordRuns.length - 1] = { text: unitText.slice(lastWordRun.startOffset, characterTheRunsRead.endOffset), startOffset: lastWordRun.startOffset, endOffset: characterTheRunsRead.endOffset };
            } else {
                wordRuns.push({ text: unitText.slice(characterTheRunsRead.startOffset, characterTheRunsRead.endOffset), startOffset: characterTheRunsRead.startOffset, endOffset: characterTheRunsRead.endOffset });
            }
        }
        return wordRuns;
    }

    /**
     * The characters of one diff unit as the content-word runs read them: each character
     * with the marks stored after it (`CHARACTER_WITH_ITS_MARKS`) is one character where
     * the fold (`comparisonKey`) composes them into one, and its characters one by one
     * where it does not.
     *
     * WHY composed marks are the letter's
     *   The fold reads "e" + U+0301 as "é": one text stored two ways. Read as letters and
     *   digits alone, a decomposed "Résumé" was cut at its accents into "Re" and "sume",
     *   so "Résumés" wrote neither back and its link was struck, where the composed
     *   word's link is kept (F2).
     *
     * WHY a mark the fold leaves a mark still cuts the word
     *   A Devanagari, Thai or Arabic vowel sign is no other spelling of its letter, and
     *   the striker keeps or strikes such a link on the fragments between the signs:
     *   "अनुबंध" reads "अन", "ब" and "ध". What a link does is parked (report item 14), so
     *   the outcomes those fragments give stay as they are; the branch of consistent link
     *   outcomes (option b) reads the word whole.
     *
     * EXAMPLE
     *   unit "Re\u{301}s"    -> R 0..1 letter, e + U+0301 1..3 letter (read "é"), s 3..4 letter
     *   unit "नु"           -> न 0..1 letter, U+0941 1..2 not a letter: the fold keeps the sign
     */
    private static charactersTheRunsRead(unitText: string): CharacterTheRunsRead[] {
        return Array.from(unitText.matchAll(CHARACTER_WITH_ITS_MARKS)).flatMap((characterWithItsMarks) => {
            const clusterStartOffset = characterWithItsMarks.index ?? 0;
            const clusterAsTheFoldReadsIt = WordTokenizer.comparisonKey(characterWithItsMarks[0]);
            /**
             * WHEN the fold reads the character and its marks as one character, "e" + U+0301 as "é"
             *   CONSEQUENCE
             *     the whole cluster is that one character
             *
             * EXAMPLE
             *   "e\u{301}" at 1, length 2 -> 1..3, `clusterStartOffset + length` being the offset just after the accent
             */
            if (Array.from(clusterAsTheFoldReadsIt).length === 1) {
                return [{ isLetterOrDigit: LETTER_OR_DIGIT.test(clusterAsTheFoldReadsIt), startOffset: clusterStartOffset, endOffset: clusterStartOffset + characterWithItsMarks[0].length }];
            }
            let characterStartOffset = clusterStartOffset;
            return Array.from(characterWithItsMarks[0], (characterOfCluster): CharacterTheRunsRead => {
                /**
                 * EXAMPLE
                 *   "नु" at 1: "न" 1..2, then the sign U+0941 2..3; `+ length` steps past each
                 *   character, two for one outside the Basic Multilingual Plane
                 */
                const characterRead = { isLetterOrDigit: LETTER_OR_DIGIT.test(characterOfCluster), startOffset: characterStartOffset, endOffset: characterStartOffset + characterOfCluster.length };
                characterStartOffset += characterOfCluster.length;
                return characterRead;
            });
        });
    }

    /**
     * The word split where the punctuation at its end begins, so a change that only
     * adds, removes or swaps that punctuation can be drawn on the punctuation alone.
     * Words are still cut on whitespace everywhere; this split is never a token.
     *
     * EXAMPLE
     *   word               textBeforeTrailingPunctuation   trailingPunctuation
     *   implementation,    implementation                  ,
     *   manner".           manner                          ".
     *   i.e.,              i.e                             .,
     *   (30)               (30                             )
     *   Nestlé,      Nestlé                    ,
     *
     * NULL
     *   the word holds no letter or digit (a dash, a lone bracket): no part of it
     *   stays when its punctuation changes.
     */
    static splitTrailingPunctuation(word: string): WordWithTrailingPunctuation | null {
        const lastWordCharacterMatch = LAST_WORD_CHARACTER_AND_TRAILING_PUNCTUATION.exec(word);
        if (lastWordCharacterMatch === null) return null;
        /**
         * EXAMPLE
         *   word = "manner\"."  match at 5, the "r"
         *   `5 + 1` -> 6, the first character after the last letter: the punctuation starts there
         */
        const trailingPunctuationStart = lastWordCharacterMatch.index + 1;
        return { textBeforeTrailingPunctuation: word.slice(0, trailingPunctuationStart), trailingPunctuation: word.slice(trailingPunctuationStart) };
    }

    /**
     * Whether `text` ends a sentence: its last marks, read through the fold
     * (`comparisonKey`), are a sentence terminal and then only closing quotes and
     * brackets (`SENTENCE_TERMINAL_THEN_CLOSERS`). The one reading of a sentence end
     * for the planner's kept end mark and its joined sentences.
     *
     * EXAMPLE
     *   ".“" (German, after „dreißig Tage)  -> true: folded ".\""
     *   "．" (full width)                     -> true: folded "."
     *   ".”"  "?"  "。」"                      -> true
     *   ","  ")"  ";"                          -> false
     */
    static endsSentence(text: string): boolean {
        return SENTENCE_TERMINAL_THEN_CLOSERS.test(WordTokenizer.comparisonKey(text));
    }

    /**
     * Whether `text` ends a sentence, or a clause closed by a semicolon, read through the
     * fold as `endsSentence` reads it. The collision reader counts a clause the legal
     * text strings with ";" as a sentence of its own.
     *
     * EXAMPLE
     *   "interest;"  "interest；"  "pay."  "とする。」"  -> true      "terms:"  "fees,"  -> false
     */
    static endsSentenceOrSemicolonClause(text: string): boolean {
        return SENTENCE_TERMINAL_OR_SEMICOLON_THEN_CLOSERS.test(WordTokenizer.comparisonKey(text));
    }

    /**
     * Whether `character` is a mark a sentence writes once and never twice in a row
     * (`TERMINAL_PUNCTUATION`), read through the fold: "，" is the comma it folds onto.
     *
     * EXAMPLE
     *   "."  ","  "，"  "。"  "、"  "?"  -> true      "'"  "-"  ")"  "a"  -> false
     */
    static isPunctuationWrittenOnce(character: string): boolean {
        return TERMINAL_PUNCTUATION.test(WordTokenizer.comparisonKey(character));
    }

    /**
     * Whether `mark` closes a sentence or a clause in a form a space follows before the
     * next word: a sentence terminal, a semicolon or a colon
     * (`MARK_CLOSING_SENTENCE_OR_CLAUSE`), written as the keyboard writes it, and not a
     * mark of a script written without spaces.
     *
     * WHY not a full-width or an ideographic mark
     *   Chinese and Japanese set "．", "；", "：" and "。" with their own space and write the
     *   next word straight after them, a Latin word too: "本契約は発効する。Customerは".
     *   A full-width mark is one the fold reads as its ASCII mark (`comparisonKey` reads
     *   "．" as "."); an ideographic one is of a script written without spaces
     *   (`CHARACTER_OF_SCRIPT_WITHOUT_SPACES`).
     *
     * EXAMPLE
     *   "."  "?"  ";"  ":"  "।"  "؟"  -> true
     *   "．"  "；"  "。"  ","  "a"      -> false
     */
    static closesSentenceOrClauseBeforeASpace(mark: string): boolean {
        /** WHEN the fold reads the mark as another, a full-width "．" read as "." CONSEQUENCE it carries its own space, so none follows it. */
        if (WordTokenizer.comparisonKey(mark) !== mark) return false;
        return MARK_CLOSING_SENTENCE_OR_CLAUSE.test(mark) && !CHARACTER_OF_SCRIPT_WITHOUT_SPACES.test(mark);
    }

    /**
     * Whether `character` is a letter of a script that separates its words with spaces:
     * Latin, Cyrillic, Greek, Hangul, Devanagari, Arabic and the rest, never a letter of
     * Chinese, Japanese, Thai, Lao, Khmer or Myanmar (`CHARACTER_OF_SCRIPT_WITHOUT_SPACES`).
     *
     * WHY only these letters glue
     *   Two letters of a script written without spaces meeting, or one of them meeting a
     *   Latin letter, is how such text is written ("ユーザーID"), never a glued word.
     *
     * EXAMPLE
     *   "a"  "Ж"  "한"  "अ"  -> true      "本"  "ー"  "ก"  "1"  "."  -> false
     */
    static isLetterOfSpacedScript(character: string): boolean {
        return ONE_LETTER.test(character) && !CHARACTER_OF_SCRIPT_WITHOUT_SPACES.test(character);
    }

    /**
     * The whitespace-insensitive form of a text: its words joined by single
     * spaces. Used wherever two texts are compared for "says the same thing",
     * because Word rewrites spacing freely and a request typed by a person or
     * a model never reproduces a paragraph's tabs and double spaces.
     */
    static collapse(text: string): string {
        return WordTokenizer.words(text).join(" ");
    }

    /**
     * Whether one character is whitespace under the fold (`MATCHED_AS_WHITESPACE`): a
     * space, a tab, a line break, the no-break space and the rest. The one test the
     * engine, the operations and the planner make for "a gap between words stands
     * here", so a tab is a gap wherever a space is.
     *
     * EXAMPLE
     *   " " -> true     "\t" -> true     "\n" -> true     "d" -> false     "" -> false
     */
    static isWhitespace(character: string): boolean {
        return character.length > 0 && MATCHED_AS_WHITESPACE.test(character);
    }

    /**
     * Whether a text is whitespace and nothing else: the one piece a revert strikes
     * beside an insertion (`RedlineEngine`). An empty text is not: there is nothing
     * in it to strike.
     *
     * EXAMPLE
     *   " "  -> true     " \t" -> true     "" -> false     " a" -> false
     */
    static isWhitespaceOnly(text: string): boolean {
        return text.length > 0 && Array.from(text).every((character) => MATCHED_AS_WHITESPACE.test(character));
    }

    /**
     * Whether a text holds a letter or a digit anywhere: a word, rather than
     * punctuation or spacing alone. The planner's and the striker's one test for
     * "this writes a word".
     *
     * EXAMPLE
     *   "work" -> true     "2^10" -> true     ", " -> false     "" -> false
     */
    static holdsALetterOrDigit(text: string): boolean {
        return LETTER_OR_DIGIT.test(text);
    }

    /**
     * How many whitespace characters stand right before `offset`: the run that ends
     * at it.
     *
     * EXAMPLE
     *   "within sixty", offset 7   -> text[`7 - 1 - 0`] = text[6] " " counted; text[5] "n" stops -> 1
     *   "within\t sixty", offset 8 -> text[7] " " and text[6] "\t" counted; text[5] "n" stops   -> 2
     *   "Pay sixty", offset 0      -> `0 - 1 - 0` is before the text's start                    -> 0
     */
    static whitespaceRunLengthEndingAt(text: string, offset: number): number {
        let runLength = 0;
        while (offset - 1 - runLength >= 0 && MATCHED_AS_WHITESPACE.test(text[offset - 1 - runLength])) runLength += 1;
        return runLength;
    }

    /**
     * How many whitespace characters stand at `offset` and after it: the run that
     * starts there.
     *
     * EXAMPLE
     *   "sixty days", offset 5  -> text[`5 + 0`] " " counted; text[6] "d" stops -> 1
     *   "sixty  days", offset 5 -> text[5] " " and text[6] " " counted           -> 2
     *   "sixty", offset 5       -> `5 + 0` is the text's end                     -> 0
     */
    static whitespaceRunLengthStartingAt(text: string, offset: number): number {
        let runLength = 0;
        while (offset + runLength < text.length && MATCHED_AS_WHITESPACE.test(text[offset + runLength])) runLength += 1;
        return runLength;
    }

    /**
     * The key two words are compared by: the word's folded form. It is only ever
     * compared, never written: the document keeps the form it holds and the request's
     * text is written as the request spells it.
     *
     * The fold, character by character
     *   - Unicode's composed form (NFC): "é" and "e" + U+0301, "が" and "か" + U+3099, a
     *     Hangul syllable and its jamo, the ångström sign U+212B and "Å" U+00C5 are one
     *     character
     *   - the typographic marks a model standardises when it retypes a quote, folded onto
     *     the mark a keyboard produces (`FOLDED_PUNCTUATION`): the single quotes and the
     *     apostrophe to ', the double quotes to ", every dash to -, a full-width
     *     punctuation mark to its ASCII mark
     *   - every run of whitespace (`MATCHED_AS_WHITESPACE`) to one space, so a quote
     *     typed with one space names a span the document wrote with a line break or two
     *
     * WHY
     *   A document and a request often hold different characters that read and print
     *   the same, so a raw comparison strikes a word and reinserts it unchanged (R13,
     *   2026-10-16: "days’ notice" -> "days' written notice" struck `’` and inserted
     *   `'`). Read through one key, every reading of the text, the anchor finder, the
     *   planner, the landing audit and the collision reader, agrees on what is one word.
     *
     * EDGE CASE
     *   Full-width and half-width letters, circled digits and ligatures are different
     *   characters that only look alike (NFKC folds them); they stay different words, so
     *   a request that changes "ＡＢＣ" to "ABC" is a change. A line break folds to a
     *   space here as it does when an anchor is found; where a line break must stay a
     *   change, the whitespace is read with `whitespaceReadsTheSame`.
     *
     * EXAMPLE
     *   "Supplier’s"   -> "Supplier's"      "—" -> "-"      "，" -> ","
     *   "Nestlé" -> "Nestlé"          "a  b" -> "a b"
     */
    static comparisonKey(word: string): string {
        /** WHEN the word is printable ASCII with no whitespace CONSEQUENCE nothing in it folds: it is its own key, and the common case costs nothing. */
        if (PLAIN_ASCII_WORD.test(word)) return word;
        let key = "";
        let previousCharacterWasWhitespace = false;
        for (const character of word.normalize("NFC")) {
            const characterIsWhitespace = MATCHED_AS_WHITESPACE.test(character);
            /**
             * CASES
             *   - whitespace after whitespace       -> nothing: the run is already one space
             *   - whitespace, the first of its run  -> " "
             *   - a mark of `FOLDED_PUNCTUATION`    -> the mark a keyboard produces
             *   - any other character               -> itself, in composed form
             */
            if (characterIsWhitespace && previousCharacterWasWhitespace) continue;
            key += characterIsWhitespace ? " " : (FOLDED_PUNCTUATION.get(character) ?? character);
            previousCharacterWasWhitespace = characterIsWhitespace;
        }
        return key;
    }

    /**
     * The key two words are compared by where only their content matters: the
     * comparison key in lower case with the punctuation at the word's edges taken
     * off. The landing audit, the collision groups and the link striker read a word
     * this way, so a comma the model moved or a capital it changed does not read as
     * the word missing.
     *
     * EXAMPLE
     *   "(45),"     -> "45"         "“Order”,"   -> "order"       "Company’s" -> "company's"
     *   "。"        -> "":  a unit that is only punctuation has no content
     */
    static contentKey(word: string): string {
        return WordTokenizer.comparisonKey(word).toLowerCase().replace(PUNCTUATION_AT_WORD_EDGES, "");
    }

    /**
     * The web addresses a text writes as words: each word (`words`) that, with the
     * punctuation at its edges taken off as for its content key, opens with "http://"
     * or "https://". The edit operations ask about an address written as plain words,
     * which reads as an address and opens nothing.
     *
     * EXAMPLE
     *   "see (https://x.example/fee)."   -> ["https://x.example/fee"]
     *   "at www.example.com or x.com"    -> []: no scheme, so no address a reader opens
     */
    static webAddressesIn(text: string): string[] {
        return WordTokenizer.words(text)
            .map((word) => word.replace(PUNCTUATION_AT_WORD_EDGES, ""))
            .filter((wordWithoutEdgePunctuation) => WEB_ADDRESS_SCHEME.test(wordWithoutEdgePunctuation));
    }

    /**
     * Whether two texts say the same thing: the same words, by `comparisonKey`, in
     * the same order, whatever their spacing. The one test the engine, the collision
     * reader, the proposal builder and the add-in make before deciding a request
     * writes nothing or a paragraph already reads as a card.
     *
     * EXAMPLE
     *   "1 Main Street London"   "1 Main Street\nLondon"   -> true
     *   "days’ notice"           "days' notice"            -> true
     *   "the cat sat"            "the dog sat"             -> false
     *   "本製品の"                 "本製品 の"                -> false: the space splits a word in two
     */
    static readsTheSame(leftText: string, rightText: string): boolean {
        return WordTokenizer.readingKey(leftText) === WordTokenizer.readingKey(rightText);
    }

    /**
     * The key under which two texts read the same (`readsTheSame`): the text's words
     * (`collapse`), each under `comparisonKey`. A caller aligning two lists of texts
     * compares their keys, and two texts with one key read the same.
     *
     * EXAMPLE
     *   "1 Main Street\nLondon"  -> "1 Main Street London"
     *   "days’ notice"           -> "days' notice"
     *   "Ex.\t3"                 -> "Ex. 3"
     */
    static readingKey(text: string): string {
        return WordTokenizer.comparisonKey(WordTokenizer.collapse(text));
    }

    /**
     * Whether two runs of whitespace between the same two words read the same to the
     * model: both or neither hold a line break, and both or neither are empty.
     *
     * WHY a line break is not a space here
     *   The prompt shows a line break as a line break (`<br/>` in the tracked view, a
     *   newline in the accepted text) and tells the model to write one wherever a break
     *   stays, so a space written in its place is the model's change. A run of spaces,
     *   a non-breaking space, a tab or the two spaces after a sentence is one the model
     *   retyped as a space, and the paragraph's own run stays.
     *
     * WHY an empty run is not a space
     *   Two Japanese words stand with nothing between them; a space written there
     *   splits a word, which is a change.
     *
     * EXAMPLE
     *   "  " and " "    -> true       " " and " " -> true      "\t" and " " -> true
     *   "\n" and " "    -> false      "\n  " and "\n"  -> true      "" and " "   -> false
     */
    static whitespaceReadsTheSame(leftWhitespace: string, rightWhitespace: string): boolean {
        const bothOrNeitherEmpty = (leftWhitespace.length === 0) === (rightWhitespace.length === 0);
        const bothOrNeitherBreakTheLine = LINE_BREAK_IN_WHITESPACE.test(leftWhitespace) === LINE_BREAK_IN_WHITESPACE.test(rightWhitespace);
        return bothOrNeitherEmpty && bothOrNeitherBreakTheLine;
    }

    /**
     * `text` folded as `comparisonKey` folds it, carrying the map back to where each
     * folded character came from.
     *
     * The map is what makes the fold safe to match on: a hit in the folded string is
     * turned back into a span of the original, so nothing downstream ever sees the
     * folded form.
     *
     * EXAMPLE
     *   text   = "a — b"          (a, non-breaking space, em dash, space, b)
     *   folded = "a - b"
     *   folded index 1 (the space) came from source 1..2, the non-breaking space alone
     *   folded index 2 (the hyphen) came from source 2..3, the em dash
     *   folded index 3 (the space) came from source 3..4
     *
     *   text   = "Nestlé"             (an e followed by a combining acute accent)
     *   folded = "Nestlé"              (the one character é)
     *   folded index 5 (the é) came from source 5..7, the e and its accent
     *
     *   text   = 한국 stored as its six jamo, U+1112 U+1161 U+11AB U+1100 U+116E U+11A8
     *   folded = 한국, the two syllables U+D55C U+AD6D
     *   folded index 0 came from source 0..3, folded index 1 from source 3..6
     *
     * WHY each grapheme cluster is folded through `comparisonKey`
     *   What a reader takes for one character is a grapheme cluster, and two clusters
     *   that read the same compare equal under the one key. Folded cluster by cluster,
     *   every character of a cluster's folded form maps to the whole cluster, so a
     *   Thai consonant with two signs is matched whole or not at all.
     */
    static foldForMatching(text: string): FoldedText {
        const foldedCharacters: string[] = [];
        const sourceStartByFoldedIndex: number[] = [];
        const sourceEndByFoldedIndex: number[] = [];
        let previousClusterWasWhitespace = false;
        for (const cluster of WordTokenizer.graphemeClustersOf(text)) {
            /**
             * EXAMPLE
             *   cluster e + U+0301 starting at 8, length 2 -> clusterEnd 10, the first character after it
             */
            const clusterEnd = cluster.startOffset + cluster.text.length;
            const clusterIsWhitespace = Array.from(cluster.text).every((character) => MATCHED_AS_WHITESPACE.test(character));
            /**
             * WHEN a run of whitespace of any length and any kind goes on here
             *   CONSEQUENCE
             *     the whole run is one space, mapped to the run's full extent, so a quote
             *     typed with one space still names a span the document wrote with a line
             *     break, an ideographic space or two spaces.
             *
             * CASES
             *   - the cluster before was whitespace too -> the run's one space now ends where this cluster ends
             *   - it was not                             -> this cluster opens the run with a space
             *
             * EXAMPLE
             *   "a", " ", U+3000, "b": the space at 1 opens the run (source 1..2), U+3000 at 2 extends it to 1..3;
             *   `sourceEndByFoldedIndex.length - 1` is the run's own space, the last folded character so far
             */
            if (clusterIsWhitespace) {
                if (previousClusterWasWhitespace) {
                    sourceEndByFoldedIndex[sourceEndByFoldedIndex.length - 1] = clusterEnd;
                } else {
                    foldedCharacters.push(" ");
                    sourceStartByFoldedIndex.push(cluster.startOffset);
                    sourceEndByFoldedIndex.push(clusterEnd);
                }
                previousClusterWasWhitespace = true;
                continue;
            }
            previousClusterWasWhitespace = false;
            /**
             * The cluster as the fold reads it (`comparisonKey`), every character of which is
             * mapped to the whole cluster.
             *
             * EXAMPLE
             *   full-width comma U+FF0C           -> ","            one character
             *   e + U+0301                        -> U+00E9         one character, mapped to both source characters
             *   the jamo U+1112 U+1161 U+11AB     -> U+D55C         one syllable, mapped to the three jamo
             *   a Thai consonant with two signs   -> as stored      three characters, each mapped to the whole cluster
             */
            const foldedCluster = WordTokenizer.comparisonKey(cluster.text);
            for (const foldedCharacter of foldedCluster) {
                foldedCharacters.push(foldedCharacter);
                sourceStartByFoldedIndex.push(cluster.startOffset);
                sourceEndByFoldedIndex.push(clusterEnd);
            }
        }
        return { text: foldedCharacters.join(""), sourceStartByFoldedIndex, sourceEndByFoldedIndex };
    }

    /**
     * The grapheme clusters of `text`, in order, each with the offset it starts at: what a
     * reader takes for one character, such as a letter with its marks, a Hangul syllable
     * stored as its jamo, or a carriage return with its line feed.
     *
     * FALLBACK
     *   A host with no `Intl.Segmenter` cuts each character with the marks that follow it,
     *   the clusters this fold composed before it read graphemes.
     */
    private static graphemeClustersOf(text: string): GraphemeCluster[] {
        if (typeof Intl.Segmenter !== "function") return Array.from(text.matchAll(CHARACTER_WITH_ITS_MARKS), (match) => ({ text: match[0], startOffset: match.index }));
        WordTokenizer.graphemeSegmenter ??= new Intl.Segmenter(undefined, { granularity: "grapheme" });
        return Array.from(WordTokenizer.graphemeSegmenter.segment(text), (segment) => ({ text: segment.segment, startOffset: segment.index }));
    }
}
